import { useCallback } from "react";
import usePagedList from "../../../hooks/usePagedList";
import * as crmApi from "../api/crmApi";
import { PAGE_SIZE } from "../contract";

/**
 * The Packs catalogue (`GET /api/crm/packs?status`, design §E.8/§G): active packs by
 * default, both statuses once "Show archived" is on — the server's `status` filter is
 * omitted rather than sent `"active,archived"`, since the contract only names one value.
 *
 * `create`/`update`/`archive`/`unarchive` write through and `reload()` the list rather
 * than patching the row locally: `active_clients` and `status` are the source of the
 * PacksTable's own reads, and this catalogue is small enough that a full reread is cheap.
 */
export default function usePacks({ includeArchived = false } = {}) {
  const query = includeArchived ? {} : { status: "active" };
  const list = usePagedList(crmApi.listPacks, query, PAGE_SIZE);

  const create = useCallback(async (payload) => {
    const pack = await crmApi.createPack(payload);
    await list.reload();
    return pack;
  }, [list]);

  const update = useCallback(async (id, payload) => {
    const pack = await crmApi.updatePack(id, payload);
    await list.reload();
    return pack;
  }, [list]);

  const archive = useCallback(async (id) => { await crmApi.archivePack(id); await list.reload(); }, [list]);
  const unarchive = useCallback(async (id) => { await crmApi.unarchivePack(id); await list.reload(); }, [list]);

  return { ...list, create, update, archive, unarchive };
}
